import { games as defaultGames, type Game } from '../mocks/games';

export const ADMIN_EMAIL = 'websitetrochoidangianbahnar@gmail.com';
export const ADMIN_PASSWORD = 'Bahnar@2024';

const STORE_KEY = 'bahnar_admin_store';
const AUTH_KEY = 'bahnar_admin_auth';

export interface LessonPlan {
  id: string;
  title: string;
  subject: string;
  duration: string;
  grade: string;
  author: string;
  description: string;
  tags: string[];
}

export interface HomeContent {
  heroTitle: string;
  heroDescription: string;
  introText: string;
}

export interface VideoItem {
  id: string;
  title: string;
  youtubeId?: string;
  localUrl?: string;
  type: 'youtube' | 'local';
  description?: string;
}

export interface AdminStoreData {
  home: HomeContent;
  hiddenGameIds: string[];
  customGames: Game[];
  lessonPlans: LessonPlan[];
  hiddenPostIds: string[];
  videos: VideoItem[];
}

export const DEFAULT_HOME: HomeContent = {
  heroTitle: 'Khám phá\ntrò chơi dân gian\nBahnar',
  heroDescription: 'Cùng bạn bè khám phá văn hóa và những trò chơi vui nhộn của người Bahnar',
  introText:
    'Website "Túi trò chơi dân gian Bahnar" là không gian giáo dục trực tuyến giới thiệu các trò chơi dân gian của dân tộc Bahnar thông qua hình thức tương tác. Nội dung được xây dựng bám sát chương trình Hoạt động trải nghiệm, kết hợp mô phỏng các trò chơi nhằm giúp học sinh tiếp cận kiến thức văn hóa, đồng thời rèn luyện các kỹ năng cơ bản phù hợp với lứa tuổi.',
};

export const DEFAULT_LESSON_PLANS: LessonPlan[] = [];

export const DEFAULT_VIDEOS: VideoItem[] = [
  {
    id: 'v1',
    title: 'Lễ hội cồng chiêng Tây Nguyên',
    youtubeId: 'XnnqTMVj5vs',
    type: 'youtube',
    description: 'Lễ hội cồng chiêng đặc sắc của đồng bào Tây Nguyên',
  },
  {
    id: 'v2',
    title: 'Nghề dệt thổ cẩm của người Bahnar',
    youtubeId: 'vJslLE8V1jI',
    type: 'youtube',
    description: 'Khám phá nghề dệt thổ cẩm truyền thống của người Bahnar',
  },
  {
    id: 'v3',
    title: 'Kiến trúc nhà Rông Bahnar',
    youtubeId: 'QoGiHmXPZ5g',
    type: 'youtube',
    description: 'Tìm hiểu về kiến trúc độc đáo của nhà Rông Bahnar',
  },
];

function getStore(): AdminStoreData {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AdminStoreData>;
      return {
        home: parsed.home ?? { ...DEFAULT_HOME },
        hiddenGameIds: parsed.hiddenGameIds ?? [],
        customGames: parsed.customGames ?? [],
        lessonPlans: parsed.lessonPlans ?? [...DEFAULT_LESSON_PLANS],
        hiddenPostIds: parsed.hiddenPostIds ?? [],
        videos: parsed.videos ?? [...DEFAULT_VIDEOS],
      };
    }
  } catch {
    /* ignore */
  }
  return {
    home: { ...DEFAULT_HOME },
    hiddenGameIds: [],
    customGames: [],
    lessonPlans: [...DEFAULT_LESSON_PLANS],
    hiddenPostIds: [],
    videos: [...DEFAULT_VIDEOS],
  };
}

function saveStore(data: AdminStoreData): void {
  localStorage.setItem(STORE_KEY, JSON.stringify(data));
  window.dispatchEvent(new Event('bahnar-admin-updated'));
}

export const adminStore = {
  isAuthenticated(): boolean {
    return localStorage.getItem(AUTH_KEY) === 'true';
  },
  login(email: string, password: string): boolean {
    if (
      email.trim().toLowerCase() === ADMIN_EMAIL.toLowerCase() &&
      password === ADMIN_PASSWORD
    ) {
      localStorage.setItem(AUTH_KEY, 'true');
      return true;
    }
    return false;
  },
  logout(): void {
    localStorage.removeItem(AUTH_KEY);
  },
  getStore,
  updateHome(home: HomeContent): void {
    saveStore({ ...getStore(), home });
  },
  updateLessonPlans(lessonPlans: LessonPlan[]): void {
    saveStore({ ...getStore(), lessonPlans });
  },
  updateVideos(videos: VideoItem[]): void {
    saveStore({ ...getStore(), videos });
  },
  toggleGameVisibility(gameId: string): void {
    const store = getStore();
    const hiddenGameIds = store.hiddenGameIds.includes(gameId)
      ? store.hiddenGameIds.filter((id) => id !== gameId)
      : [...store.hiddenGameIds, gameId];
    saveStore({ ...store, hiddenGameIds });
  },
  addCustomGame(game: Game): void {
    const store = getStore();
    saveStore({ ...store, customGames: [...store.customGames, game] });
  },
  deleteCustomGame(gameId: string): void {
    const store = getStore();
    saveStore({ ...store, customGames: store.customGames.filter((g) => g.id !== gameId) });
  },
  hidePost(postId: string): void {
    const store = getStore();
    const hiddenPostIds = [...new Set([...store.hiddenPostIds, postId])];
    saveStore({ ...store, hiddenPostIds });
  },
  restorePost(postId: string): void {
    const store = getStore();
    saveStore({ ...store, hiddenPostIds: store.hiddenPostIds.filter((id) => id !== postId) });
  },
  getVisibleGames(): Game[] {
    const store = getStore();
    return [...defaultGames, ...store.customGames].filter(
      (g) => !store.hiddenGameIds.includes(g.id)
    );
  },
};
